/**
 * Safe Storage Utilities
 * 
 * Purpose: Provide secure, type-safe access to localStorage/sessionStorage
 * Threats mitigated:
 * - XSS attacks accessing stored tokens
 * - Type errors from malformed stored data
 * - Storage quota errors
 */

import { logError } from '@/utils/logger'

const STORAGE_PREFIX = 'stide_'

/**
 * Safe localStorage wrapper with error handling
 */
export const safeStorage = {
  /**
   * Sets an item in localStorage with error handling
   */
  setItem<T>(key: string, value: T): boolean {
    try {
      const serialized = JSON.stringify(value)
      localStorage.setItem(`${STORAGE_PREFIX}${key}`, serialized)
      return true
    } catch (error) {
      logError('Failed to store item in local storage', error)
      return false
    }
  },

  /**
   * Gets an item from localStorage with type safety
   */
  getItem<T>(key: string, defaultValue: T | null = null): T | null {
    try {
      const item = localStorage.getItem(`${STORAGE_PREFIX}${key}`)
      if (item === null) {
        return defaultValue
      }
      return JSON.parse(item) as T
    } catch (error) {
      logError('Failed to retrieve item from local storage', error)
      return defaultValue
    }
  },

  /**
   * Removes an item from localStorage
   */
  removeItem(key: string): void {
    try {
      localStorage.removeItem(`${STORAGE_PREFIX}${key}`)
    } catch (error) {
      logError('Failed to remove item from local storage', error)
    }
  },

  /**
   * Clears all app-related items from localStorage
   */
  clear(): void {
    try {
      const keys = Object.keys(localStorage)
      keys.forEach((key) => {
        if (key.startsWith(STORAGE_PREFIX)) {
          localStorage.removeItem(key)
        }
      })
    } catch (error) {
      logError('Failed to clear storage:', error)
    }
  },
}

/**
 * Safe sessionStorage wrapper
 */
export const safeSessionStorage = {
  setItem<T>(key: string, value: T): boolean {
    try {
      const serialized = JSON.stringify(value)
      sessionStorage.setItem(`${STORAGE_PREFIX}${key}`, serialized)
      return true
    } catch (error) {
      logError('Failed to store item in session storage', error)
      return false
    }
  },

  getItem<T>(key: string, defaultValue: T | null = null): T | null {
    try {
      const item = sessionStorage.getItem(`${STORAGE_PREFIX}${key}`)
      if (item === null) {
        return defaultValue
      }
      return JSON.parse(item) as T
    } catch (error) {
      logError('Failed to retrieve item from session storage', error)
      return defaultValue
    }
  },

  removeItem(key: string): void {
    try {
      sessionStorage.removeItem(`${STORAGE_PREFIX}${key}`)
    } catch (error) {
      logError('Failed to remove item from session storage', error)
    }
  },
}

